/**
 * Role-based access control utilities
 */

export type AppRole =
  | 'super_admin'
  | 'admin'
  | 'branch_admin'
  | 'branch_staff'
  | 'staff'
  | 'customer'
  | 'rider'
  | 'kitchen'
  | 'customer_service'
  | 'dispatcher';

// Admin routes accessible only by super_admin and admin
const ADMIN_ONLY_ROUTES = [
  '/admin/user-management',
  '/admin/analytics',
  '/admin/branches',
  '/admin/products',
  '/admin/categories',
];

// Routes accessible by specific roles
export const ROLE_ROUTES: Record<AppRole, string[]> = {
  super_admin: ['*'], // All routes
  admin: ['*'], // All routes

  // Legacy roles (for backward compatibility)
  branch_admin: [
    '/admin/dashboard',
    '/admin/orders',
    '/admin/kitchen-display',
    '/admin/dispatcher',
    '/admin/customer-service',
    '/admin/riders',
  ],
  staff: [
    '/admin/dashboard',
    '/admin/orders',
    '/admin/kitchen-display',
    '/admin/dispatcher',
    '/admin/customer-service',
  ],
  branch_staff: [
    '/admin/dashboard',
    '/admin/orders',
    '/admin/kitchen-display',
    '/admin/dispatcher',
    '/admin/customer-service',
  ],

  // Department-specific roles
  rider: [
    '/admin/rider-dashboard',
    '/admin/rider-view',
    '/admin/settings', // Only their own profile/settings
  ],
  kitchen: [
    '/admin/kitchen-display',
    '/admin/orders', // View only, limited actions
    '/admin/settings',
  ],
  customer_service: [
    '/admin/customer-service',
    '/admin/orders', // Can edit orders
    '/admin/place-order', // Can place orders on behalf of customers
    '/admin/settings',
  ],
  dispatcher: [
    '/admin/dispatcher',
    '/admin/riders',
    '/admin/orders', // View for dispatch
    '/admin/place-order', // Can place orders for dispatch purposes
    '/admin/cash-tracking', // Cash remission and tracking
    '/admin/cash-submissions', // Cash submission management
    '/admin/settings',
  ],

  customer: [], // No admin access
};

// Features/actions accessible by role
export const ROLE_PERMISSIONS = {
  // Order management
  canViewAllOrders: ['super_admin', 'admin', 'branch_admin', 'staff', 'branch_staff', 'customer_service', 'dispatcher'],
  canEditOrders: ['super_admin', 'admin', 'branch_admin', 'staff', 'branch_staff', 'customer_service'],
  canDeleteOrders: ['super_admin', 'admin'],
  canAssignRiders: ['super_admin', 'admin', 'branch_admin', 'dispatcher'],

  // User management
  canManageUsers: ['super_admin', 'admin'],
  canManageRoles: ['super_admin', 'admin'],
  canViewUsers: ['super_admin', 'admin', 'customer_service'],

  // Kitchen
  canAccessKitchen: ['super_admin', 'admin', 'branch_admin', 'staff', 'branch_staff', 'kitchen'],
  canUpdateOrderStatus: ['super_admin', 'admin', 'branch_admin', 'staff', 'branch_staff', 'kitchen'],

  // Dispatch
  canAccessDispatch: ['super_admin', 'admin', 'branch_admin', 'staff', 'branch_staff', 'dispatcher'],
  canManageRiders: ['super_admin', 'admin', 'dispatcher'],

  // Customer Service
  canAccessCustomerService: ['super_admin', 'admin', 'branch_admin', 'staff', 'branch_staff', 'customer_service'],
  canViewCustomerDetails: ['super_admin', 'admin', 'customer_service'],

  // Financial
  canViewFinancials: ['super_admin', 'admin'],
  canManageCashTracking: ['super_admin', 'admin'],

  // Analytics
  canViewAnalytics: ['super_admin', 'admin', 'branch_admin'],

  // Settings
  canManageSettings: ['super_admin', 'admin'],
  canEditOwnProfile: ['*'], // All authenticated users

  // Cash Tracking & Remission
  canAccessCashTracking: ['super_admin', 'admin', 'dispatcher'],
  canApproveCashSubmissions: ['super_admin', 'admin', 'dispatcher'],
  canViewCommissions: ['super_admin', 'admin', 'dispatcher'],
};

/**
 * Check if user has access to a specific route
 */
export function canAccessRoute(userRoles: AppRole[], route: string): boolean {
  if (!userRoles || userRoles.length === 0) return false;

  // Super admin and admin have access to everything
  if (userRoles.includes('super_admin') || userRoles.includes('admin')) {
    return true;
  }

  // Check if any of user's roles grant access to this route
  return userRoles.some(role => {
    const allowedRoutes = ROLE_ROUTES[role];
    if (allowedRoutes.includes('*')) return true;
    return allowedRoutes.some(allowedRoute => route.startsWith(allowedRoute));
  });
}

/**
 * Check if user has a specific permission
 */
export function hasPermission(userRoles: AppRole[], permission: keyof typeof ROLE_PERMISSIONS): boolean {
  if (!userRoles || userRoles.length === 0) return false;

  const allowedRoles = ROLE_PERMISSIONS[permission];
  if (allowedRoles.includes('*')) return true;

  return userRoles.some(role => allowedRoles.includes(role));
}

/**
 * Get the default landing page for a user based on their roles
 */
export function getDefaultLandingPage(userRoles: AppRole[]): string {
  if (!userRoles || userRoles.length === 0) return '/';

  // Priority order
  if (userRoles.includes('rider')) return '/admin/rider-dashboard';
  if (userRoles.includes('kitchen')) return '/admin/kitchen-display';
  if (userRoles.includes('customer_service')) return '/admin/customer-service';
  if (userRoles.includes('dispatcher')) return '/admin/dispatcher';
  if (userRoles.includes('super_admin') || userRoles.includes('admin')) return '/admin/dashboard';
  if (userRoles.includes('branch_admin')) return '/admin/dashboard';
  if (userRoles.includes('staff') || userRoles.includes('branch_staff')) return '/admin/dashboard';

  // Customer or unknown
  return '/';
}

/**
 * Filter navigation items based on user's roles
 */
export function getAccessibleNavItems(userRoles: AppRole[]) {
  const allNavItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: 'LayoutDashboard', roles: ['super_admin', 'admin', 'branch_admin', 'staff', 'branch_staff'] },
    { path: '/admin/orders', label: 'Orders', icon: 'ShoppingCart', roles: ['super_admin', 'admin', 'branch_admin', 'staff', 'branch_staff', 'customer_service', 'dispatcher', 'kitchen'] },
    { path: '/admin/place-order', label: 'Place Order', icon: 'Plus', roles: ['super_admin', 'admin', 'branch_admin', 'staff', 'branch_staff', 'customer_service', 'dispatcher'] },
    { path: '/admin/kitchen-display', label: 'Kitchen Display', icon: 'ChefHat', roles: ['super_admin', 'admin', 'branch_admin', 'staff', 'branch_staff', 'kitchen'] },
    { path: '/admin/dispatcher', label: 'Dispatch', icon: 'Truck', roles: ['super_admin', 'admin', 'branch_admin', 'staff', 'branch_staff', 'dispatcher'] },
    { path: '/admin/customer-service', label: 'Customer Service', icon: 'Headphones', roles: ['super_admin', 'admin', 'branch_admin', 'staff', 'branch_staff', 'customer_service'] },
    { path: '/admin/riders', label: 'Riders', icon: 'Navigation', roles: ['super_admin', 'admin', 'dispatcher'] },
  { path: '/admin/rider-view', label: 'Rider View', icon: 'Navigation', roles: ['super_admin', 'admin', 'dispatcher', 'rider'] },
  { path: '/admin/rider-dashboard', label: 'My Dashboard', icon: 'Navigation', roles: ['rider'] },
    { path: '/admin/analytics', label: 'Analytics', icon: 'BarChart', roles: ['super_admin', 'admin', 'branch_admin'] },
    { path: '/admin/user-management', label: 'Users', icon: 'Users', roles: ['super_admin', 'admin'] },
    { path: '/admin/cash-tracking', label: 'Cash Tracking', icon: 'DollarSign', roles: ['super_admin', 'admin', 'dispatcher'] },
    { path: '/admin/cash-submissions', label: 'Cash Submissions', icon: 'ClipboardList', roles: ['super_admin', 'admin', 'dispatcher'] },
    { path: '/admin/products', label: 'Products', icon: 'Package', roles: ['super_admin', 'admin'] },
    { path: '/admin/categories', label: 'Categories', icon: 'FolderTree', roles: ['super_admin', 'admin'] },
    { path: '/admin/branches', label: 'Branches', icon: 'MapPin', roles: ['super_admin', 'admin'] },
    { path: '/admin/settings', label: 'Settings', icon: 'Settings', roles: ['*'] },
  ];

  return allNavItems.filter(item => {
    if (item.roles.includes('*')) return true;
    if (userRoles.includes('super_admin') || userRoles.includes('admin')) return true;
    return userRoles.some(role => item.roles.includes(role));
  });
}
